import { Button } from 'components';
import { useStore } from 'containers/Layout/store';
import { useIsConnectedAccountVip } from 'hooks/useIsConnectedAccountVip';
import { useIsUserPrime } from 'hooks/useIsUserPrime';
import { useTranslation } from 'libs/translations';
import { useAccountAddress, useAuthModal } from 'libs/wallet';
import { type FC, Suspense } from 'react';
import { safeLazyLoad } from 'utilities';
import type { AccountModalProps } from './AccountModal';
import { UserButton } from './UserButton';

// safeLazyLoad only accepts components without props, hence the type casts
const AccountModal = safeLazyLoad(
  () => import('./AccountModal') as unknown as Promise<{ default: FC<{ [k: string]: never }> }>,
) as unknown as FC<AccountModalProps>;

export const ConnectButton: React.FC = () => {
  const { accountAddress } = useAccountAddress();
  const { isConnectedAccountVip } = useIsConnectedAccountVip();

  const { t } = useTranslation();

  const { openAuthModal } = useAuthModal();

  const openModal = useStore(state => state.openModal);
  const setOpenModal = useStore(state => state.setOpenModal);

  const isModalOpen = openModal === 'accountModal';

  const handleToggleAccountModal = () => setOpenModal(isModalOpen ? undefined : 'accountModal');
  const handleCloseAccountModal = () => setOpenModal(undefined);

  const handleConnectButtonClick = () => {
    openAuthModal({
      analyticVariant: 'header_connect_button',
    });
  };

  const { isUserPrime: isAccountPrime, isLoading: isUserPrimeLoading } = useIsUserPrime({
    accountAddress,
  });

  if (isUserPrimeLoading) {
    return undefined;
  }

  if (accountAddress) {
    return (
      <div>
        <UserButton
          onClick={handleToggleAccountModal}
          address={accountAddress}
          isPrime={isAccountPrime}
          isVip={isConnectedAccountVip}
        />

        {isModalOpen && (
          <Suspense>
            <AccountModal
              address={accountAddress}
              isPrime={isAccountPrime}
              isVip={isConnectedAccountVip}
              onClose={handleCloseAccountModal}
            />
          </Suspense>
        )}
      </div>
    );
  }

  return (
    <Button onClick={handleConnectButtonClick} className="h-10 px-3 sm:h-12">
      {t('connectButton.connect')}
    </Button>
  );
};
